'use client';

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { Play, Pause, ChevronLeft, ChevronRight, Volume2, Home, Maximize2, Minimize2, Sparkles, Star, RefreshCw, ArrowRight } from 'lucide-react';
import UnifiedStoryEndModal from './UnifiedStoryEndModal';
import AudioBookProgressModal from './audio-book-progress-modal';
import Asset3DRenderer from '../3d-assets/Asset3DRenderer';
import MagicalEffect from './MagicalEffect';
import PageAtmosphere from './PageAtmosphere';
import VisualWeatherEffect from './VisualWeatherEffect';
import EpicMomentEffect from './EpicMomentEffect';
import WordHighlightedText from './WordHighlightedText';
import WordByWordCaption from './mobile/WordByWordCaption';
import BackgroundParticles from './BackgroundParticles';
import SubscriptionGate from './SubscriptionGate';

interface PageData {
  pageNum: number;
  imageUrl: string;
  text: string;
  translatedText?: string;  // FIXED: For bilingual support
  audioUrl: string;
  translatedAudioUrl?: string;  // FIXED: For bilingual support
  soundEffectUrl?: string;
  duration: number;
  isCover?: boolean;  // FIXED: To detect cover pages
  isEnding?: boolean;  // FIXED: To detect ending pages
  asset3D?: {
    assetId: string;
    glbUrl: string;
    usdzUrl?: string;
    posterUrl?: string;
    placement: 'center';
    motion: 'auto-rotate' | 'none';
    sizeHint: 'featured';
    note?: string;
  };
  magicalEffect?: {
    effectId: string;
    note?: string;
  };
  atmosphere?: {
    gradient: [string, string];
    mood: string;
    timeOfDay?: string;
    brightness: 'light' | 'medium' | 'dark';
  };
  visualEffect?: {
    type: 'rain' | 'snow' | 'sunshine' | 'lightning' | 'fog' | 'wind' | 'none';
    intensity: 'light' | 'medium' | 'heavy';
  };
  epicMoment?: {
    enabled: boolean;
    effect: 'burst' | 'cascade' | 'ripple' | 'fireworks' | 'lightning-strike';
    trigger: 'auto' | 'on-page-load';
  };
  // Audio Library System fields
  emotion?: string;
  objectSfxUrl?: string;
  bgmUrl?: string;
  bgmVolume?: number;
  wordTimings?: Array<{ w: string; start: number; end: number }>;
  translatedWordTimings?: Array<{ w: string; start: number; end: number }>; // NEW: For target language word highlighting
  // FIXED: Learning mode fields
  letterTimings?: Array<{ letter: string; start: number; end: number; wordIndex: number }>;
  translatedLetterTimings?: Array<{ letter: string; start: number; end: number; wordIndex: number }>;
}

interface AudioBookData {
  id: string;
  title: string;
  coverImageUrl: string | null;
  pagesData: PageData[];
  totalDuration: number;
  watermark?: boolean;
  status: string;
  ageCategory?: string;
  language?: string;
  bilingualMode?: boolean;
  sourceLanguage?: string;
  targetLanguage?: string;
  isLearningContent?: boolean;
  montessoriMode?: boolean;
  animationEnabled?: boolean;
  animationMetadata?: {
    enabled: boolean;
    assets: Array<any>;
  };
}

interface AudioBookViewerUnifiedProps {
  audioBookId: string;
}

export default function AudioBookViewerUnified({ audioBookId }: AudioBookViewerUnifiedProps) {
  const searchParams = useSearchParams();
  const [bookData, setBookData] = useState<AudioBookData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCover, setShowCover] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; delay: number }>>([]);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const [showStoryEndModal, setShowStoryEndModal] = useState(false);
  const [generatingBookId, setGeneratingBookId] = useState<string | null>(null);
  const [showFullscreenControls, setShowFullscreenControls] = useState(true); // Netflix-style auto-hide controls
  
  // Subscription & access control state
  const [showSubscriptionGate, setShowSubscriptionGate] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const sfxRef = useRef<HTMLAudioElement>(null);
  const bgmAudioRef = useRef<HTMLAudioElement>(null);
  const shouldPlayAfterTransition = useRef(false);
  const hasAutoStarted = useRef(false);
  const pageStartTimeRef = useRef<number>(0);
  const isAutoAdvancing = useRef(false); // Track if we're auto-advancing (BGM should continue) vs manual pause (BGM should pause)
  const controlsHideTimerRef = useRef<NodeJS.Timeout | null>(null); // Timer for auto-hiding controls
  const MIN_3D_PAGE_DURATION = 10000; // 10 seconds minimum for 3D pages
  const firstAssetPageIndex = useRef<number | null>(null); // Track first page with 3D asset
  
  // PERFORMANCE FIX: Use ref for currentTime (no re-render), only setState for activeWordIndex
  const currentTimeRef = useRef<number>(0);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const [bgmLoaded, setBgmLoaded] = useState(false);

  // 🔧 CRITICAL FIX: Restore state from viewport router on mount
  useEffect(() => {
    const handleStateRestore = (event: CustomEvent) => {
      const state = event.detail;
      console.log('[Desktop Player] 📥 Restoring state from viewport change:', state);
      
      if (state.currentPageIndex !== undefined) {
        setCurrentPageIndex(state.currentPageIndex);
      }
      if (state.hasStarted !== undefined && !state.hasStarted) {
        setShowCover(true);
      } else if (state.hasStarted !== undefined && state.hasStarted) {
        setShowCover(false);
      }
      if (state.currentTime !== undefined && audioRef.current) {
        audioRef.current.currentTime = state.currentTime;
        currentTimeRef.current = state.currentTime;
      }
      if (state.isPlaying !== undefined && audioRef.current) {
        // Wait for audio to load before trying to play
        if (state.isPlaying) {
          setTimeout(() => {
            audioRef.current?.play().catch(e => 
              console.warn('[Desktop Player] Could not auto-play after restore:', e)
            );
          }, 300);
        }
      }
    };
    
    window.addEventListener('audiobook-state-restore' as any, handleStateRestore);
    
    return () => {
      window.removeEventListener('audiobook-state-restore' as any, handleStateRestore);
    };
  }, []);
  
  // 🔧 CRITICAL FIX: Broadcast state changes for preservation across viewport changes
  useEffect(() => {
    if (!bookData) return; // Only broadcast after book is loaded
    
    const state = {
      currentPageIndex,
      isPlaying,
      currentTime: currentTimeRef.current,
      hasStarted: !showCover,
      audioSrc: bookData.pagesData?.[currentPageIndex]?.audioUrl || null
    };
    
    // Dispatch state update event
    window.dispatchEvent(new CustomEvent('audiobook-state-update', {
      detail: state
    }));
  }, [currentPageIndex, isPlaying, showCover, bookData]);

  useEffect(() => {
    // Reset refs when loading a new book
    firstAssetPageIndex.current = null;
    hasAutoStarted.current = false;
    
    fetchBookData();
    checkAccessControl();
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 6
    }));
    setParticles(newParticles);
  }, [audioBookId]);

  // ESC key handler for fullscreen
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isFullscreen]);

  // Auto-start if coming from Watch Next modal
  useEffect(() => {
    const autoplay = searchParams?.get('autoplay');
    
    if (autoplay === 'true' && bookData && !hasAutoStarted.current && !loading) {
      console.log('🎬 Auto-starting book from Watch Next');
      hasAutoStarted.current = true;
      
      // Skip cover and start playing automatically
      setTimeout(() => {
        setShowCover(false);
        setCurrentPageIndex(0);
        shouldPlayAfterTransition.current = true;
        
        // Clean up URL by removing autoplay parameter
        if (window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.delete('autoplay');
          window.history.replaceState({}, '', url.toString());
        }
      }, 500); // Small delay to ensure smooth transition
    }
  }, [bookData, loading, searchParams]);

  // Preload 3D models for upcoming pages (reduce loading delay)
  useEffect(() => {
    if (!bookData || !bookData.pagesData) return;
    
    // Preload next 3 pages with 3D assets
    const upcomingPages = bookData.pagesData.slice(
      currentPageIndex + 1,
      currentPageIndex + 4
    );
    
    upcomingPages.forEach((page) => {
      if (page.asset3D?.glbUrl) {
        // Preload GLB model in background
        fetch(page.asset3D.glbUrl, { mode: 'no-cors' }).catch(() => {
          // Silent fail - model will load when page is reached
        });
      }
    });
  }, [bookData, currentPageIndex]);

  // Audio time update listener for word highlighting
  // PERFORMANCE OPTIMIZED: Use requestAnimationFrame for smooth 60 FPS tracking
  // But ONLY trigger re-renders when the active word INDEX changes (not every frame)
  // This reduces re-renders from 60/sec to 5-10/sec, preventing CSS animation glitching
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    let animationFrameId: number;
    let lastWordIndex = -1;
    
    const updateTime = () => {
      // Update ref (no re-render)
      currentTimeRef.current = audio.currentTime;
      
      // Only update state (trigger re-render) when active word CHANGES
      const currentPage = bookData?.pagesData[currentPageIndex];
      if (currentPage?.wordTimings && isPlaying) {
        const newIndex = findActiveWordIndex(
          currentTimeRef.current,
          currentPage.wordTimings
        );
        
        if (newIndex !== lastWordIndex) {
          lastWordIndex = newIndex;
          setActiveWordIndex(newIndex);  // Only re-render when word changes
        }
      } else if (lastWordIndex !== -1) {
        // Reset when paused or no timings
        lastWordIndex = -1;
        setActiveWordIndex(-1);
      }
      
      animationFrameId = requestAnimationFrame(updateTime);
    };
    
    // Start the animation loop
    animationFrameId = requestAnimationFrame(updateTime);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [currentPageIndex, bookData, isPlaying]); // Re-attach when page changes or play state changes

  // BGM cross-fade on page change - ONLY when BGM URL changes (club-style continuity)
  useEffect(() => {
    if (!bookData || showCover) return;

    const currentPage = bookData.pagesData[currentPageIndex];
    const bgmAudio = bgmAudioRef.current;
    if (!bgmAudio) return;

    const targetVolume = currentPage?.bgmVolume || 0.10; // FIX: 10% volume (perfect balance)
    const newBgmUrl = currentPage?.bgmUrl;

    // CRITICAL FIX: Check if BGM URL actually changed
    // If same URL, just continue playing (don't restart)
    const currentSrc = bgmAudio.src;
    const isSameBgm = newBgmUrl && currentSrc && currentSrc.includes(newBgmUrl.split('/').pop() || '');

    if (newBgmUrl && !isSameBgm) {
      // BGM URL CHANGED - Do smooth crossfade
      const wasPlaying = !bgmAudio.paused;

      if (wasPlaying) {
        // Fade out current BGM smoothly
        const fadeOutInterval = setInterval(() => {
          if (bgmAudio.volume > 0.01) {
            bgmAudio.volume = Math.max(0, bgmAudio.volume - 0.02);
          } else {
            clearInterval(fadeOutInterval);

            // Switch to new BGM and fade in
            bgmAudio.src = newBgmUrl;
            bgmAudio.loop = true;
            bgmAudio.volume = 0;
            bgmAudio.load();

            // Fade in new BGM
            bgmAudio.play().then(() => {
              const fadeInInterval = setInterval(() => {
                if (bgmAudio.volume < targetVolume - 0.01) {
                  bgmAudio.volume = Math.min(targetVolume, bgmAudio.volume + 0.02);
                } else {
                  bgmAudio.volume = targetVolume;
                  clearInterval(fadeInInterval);
                }
              }, 50);
            }).catch(console.error);
          }
        }, 50);
      } else {
        // Not playing, just load new BGM and start it
        bgmAudio.src = newBgmUrl;
        bgmAudio.volume = 0;
        bgmAudio.loop = true;
        bgmAudio.load();
        // Auto-start BGM with fade-in
        bgmAudio.play().then(() => {
          const fadeInInterval = setInterval(() => {
            if (bgmAudio.volume < targetVolume - 0.01) {
              bgmAudio.volume = Math.min(targetVolume, bgmAudio.volume + 0.02);
            } else {
              bgmAudio.volume = targetVolume;
              clearInterval(fadeInInterval);
            }
          }, 50);
        }).catch(console.error);
      }
    } else if (newBgmUrl && isSameBgm) {
      // SAME BGM - Keep playing continuously, just adjust volume if needed
      if (Math.abs(bgmAudio.volume - targetVolume) > 0.01) {
        // Smooth volume adjustment
        const volumeDiff = targetVolume - bgmAudio.volume;
        const step = volumeDiff / 20; // 20 steps for smooth transition
        let count = 0;
        const volumeInterval = setInterval(() => {
          count++;
          if (count < 20) {
            bgmAudio.volume += step;
          } else {
            bgmAudio.volume = targetVolume;
            clearInterval(volumeInterval);
          }
        }, 50);
      }
      // Ensure BGM is playing if it was stopped
      if (bgmAudio.paused) {
        console.log(`[BGM] Resuming music: ${newBgmUrl.split('/').pop()}`);
        bgmAudio.play().catch(console.error);
      } else {
        console.log(`[BGM] Continuing same music across page change (${newBgmUrl.split('/').pop()})`);
      }
    } else if (!newBgmUrl && !bgmAudio.paused) {
      // No BGM on this page - fade out current music
      const fadeOutInterval = setInterval(() => {
        if (bgmAudio.volume > 0.01) {
          bgmAudio.volume = Math.max(0, bgmAudio.volume - 0.02);
        } else {
          bgmAudio.pause();
          bgmAudio.volume = 0;
          clearInterval(fadeOutInterval);
        }
      }, 50);
    }

    return () => {
      // Don't stop music on cleanup - let it continue to next page
      // Only stop when component unmounts completely
    };
  }, [currentPageIndex, bookData, showCover]);

  async function fetchBookData() {
    try {
      const response = await fetch(`/api/audio-book/${audioBookId}`);
      if (!response.ok) throw new Error('Failed to fetch book');
      
      const data = await response.json();
      console.log('[Audio Book Viewer] Book data received:', {
        id: data.id,
        title: data.title,
        pagesCount: data.pagesData?.length,
        montessoriMode: data.montessoriMode,
        animationEnabled: data.animationEnabled,
        has3DAssets: data.animationMetadata?.enabled,
        assetCount: data.animationMetadata?.assets?.filter((a: any) => a).length || 0,
      });
      
      // Check for 3D assets in pages
      if (data.pagesData) {
        const pagesWithAssets = data.pagesData.filter((p: any) => p.asset3D);
        console.log(`[Audio Book Viewer] 🌳 Pages with 3D assets: ${pagesWithAssets.length}/${data.pagesData.length}`);
        if (pagesWithAssets.length > 0) {
          pagesWithAssets.forEach((page: any, idx: number) => {
            console.log(`   📄 Page ${page.pageNum}: asset3D =`, page.asset3D);
          });
        }
      }
      
      const bookDataObj = {
        id: data.id,
        title: data.title,
        coverImageUrl: data.coverImageUrl,
        pagesData: data.pagesData,
        totalDuration: data.totalDuration || 0,
        watermark: data.watermark,
        status: data.status,
        ageCategory: data.ageCategory,
        montessoriMode: data.montessoriMode,
        animationEnabled: data.animationEnabled,
        animationMetadata: data.animationMetadata,
      };
      
      // Track the first page with a 3D asset (reset for each new book)
      if (data.pagesData) {
        const firstAssetIndex = data.pagesData.findIndex((p: any) => p.asset3D);
        firstAssetPageIndex.current = firstAssetIndex !== -1 ? firstAssetIndex : null;
        if (firstAssetIndex !== -1) {
          console.log(`[Audio Book Viewer] First 3D asset page: ${firstAssetIndex}`);
        }
      }
      setBookData(bookDataObj);
    } catch (error) {
      console.error('[Audio Book Viewer] Error fetching book:', error);
    } finally {
      setLoading(false);
    }
  }

  async function checkAccessControl() {
    try {
      // Fetch first 3 featured samples
      const samplesRes = await fetch('/api/audio-book/samples');
      const samplesData = await samplesRes.json();
      const freeSampleIds = (samplesData.audioBooks || []).map((b: any) => b.id);
      
      // Fetch book data
      const bookRes = await fetch(`/api/audio-book/${audioBookId}`);
      if (!bookRes.ok) {
        setShowSubscriptionGate(true);
        setLoading(false);
        return;
      }
      const book = await bookRes.json();
      
      // Fetch user account
      const accountRes = await fetch('/api/account');
      const accountData = accountRes.ok ? await accountRes.json() : null;
      
      const isBookOwner = accountData && book.userId === accountData.id;
      const isInFreeBooks = freeSampleIds.includes(audioBookId);
      const hasSubscription = accountData?.isSubscriber || 
                              accountData?.subscriptionPlan === 'AMAYA_PLUS' || 
                              accountData?.subscriptionPlan === 'AMAYA_PRO';
      
      setIsOwner(isBookOwner);
      setIsPro(accountData?.subscriptionPlan === 'AMAYA_PRO');
      setIsSubscriber(hasSubscription);
      
      // Access: subscribers OR owners OR featured samples
      const hasAccess = hasSubscription || isBookOwner || isInFreeBooks;
      
      setCanAccess(hasAccess);
      // DON'T show gate immediately - let users see the book first for SEO & marketing!
      // Gate will be triggered when they click Begin Adventure or Play button
    } catch (error) {
      console.error('[Desktop Unified] Access check error:', error);
    }
  }

  // 🎯 PERFORMANCE: Predictive preload system for 3D assets (N+1 & N+2)
  useEffect(() => {
    if (!bookData?.pagesData) return;
    
    // Preload next 2 pages' 3D assets for instant load
    const upcomingPages = bookData.pagesData.slice(
      currentPageIndex + 1,
      currentPageIndex + 3
    );
    
    upcomingPages.forEach((page: PageData) => {
      if (page.asset3D?.glbUrl) {
        // ✅ CRITICAL FIX: Use fetch with CORS mode instead of <link> prefetch
        // Link prefetch doesn't properly handle CORS for cross-origin resources
        fetch(page.asset3D.glbUrl, { 
          mode: 'cors',
          credentials: 'omit',
          priority: 'low' as any // Hint: low priority background fetch
        }).then(response => {
          if (response.ok) {
            console.log('[Preload] ✅ 3D asset preloaded:', page.asset3D!.assetId);
          }
        }).catch(err => {
          console.log('[Preload] ⚠️  Preload failed (not critical):', page.asset3D!.assetId);
        });
        
        // Preload USDZ for AR (iOS)
        if (page.asset3D.usdzUrl) {
          fetch(page.asset3D.usdzUrl, {
            mode: 'cors',
            credentials: 'omit',
            priority: 'low' as any
          }).catch(() => {}); // Silent fail
        }
      }
    });
    
    // Cleanup handled automatically by browser
  }, [bookData, currentPageIndex]);

  const currentPage = bookData?.pagesData[currentPageIndex];

  // Load and prepare audio when page changes
  useEffect(() => {
    if (!currentPage || showCover || isPageTransitioning) return;

    const audio = audioRef.current;
    const sfx = sfxRef.current;

    if (!audio) return;

    audio.src = currentPage.audioUrl;
    audio.load();

    // CRITICAL FIX: Support both legacy soundEffectUrl AND new objectSfxUrl
    const effectUrl = currentPage.soundEffectUrl || currentPage.objectSfxUrl;
    if (sfx && effectUrl) {
      console.log('[SFX] Loading sound effect:', effectUrl);
      sfx.src = effectUrl;
      sfx.loop = true;
      sfx.volume = 0.20; // 20% volume for balanced audio mix
      sfx.load();
    } else if (sfx) {
      // No sound effect on this page - reset
      sfx.pause();
      sfx.currentTime = 0;
      sfx.src = '';
    }

    // Auto-play if flag is set (from Begin button or auto-advance)
    if (shouldPlayAfterTransition.current) {
      // Start playing immediately (no delay)
      setIsPlaying(true);
      shouldPlayAfterTransition.current = false;
      // Reset auto-advance flag after transition completes
      setTimeout(() => {
        isAutoAdvancing.current = false;
      }, 200);
    }
  }, [currentPage, showCover, isPageTransitioning]);

  // Handle audio playback based on isPlaying state
  useEffect(() => {
    if (!currentPage || showCover || isPageTransitioning) {
      const audio = audioRef.current;
      const sfx = sfxRef.current;
      // CRITICAL FIX: DON'T pause BGM during transitions - let it continue!
      if (audio) audio.pause();
      if (sfx) sfx.pause();
      // BGM continues playing through transitions
      return;
    }

    const audio = audioRef.current;
    const sfx = sfxRef.current;
    const bgm = bgmAudioRef.current;

    if (!audio) return;

    if (isPlaying) {
      // Play narration audio
      audio.play().catch(console.error);
      
      // CRITICAL FIX: Play sound effects if available (support both fields)
      const effectUrl = currentPage.soundEffectUrl || currentPage.objectSfxUrl;
      if (sfx && effectUrl) {
        console.log('[SFX] Playing sound effect:', effectUrl);
        sfx.play().catch(console.error);
      } else {
        console.log('[SFX] No sound effect on this page');
      }
      
      // Play BGM if available (handled by BGM effect above)
      if (bgm && currentPage.bgmUrl) {
        console.log('[BGM] Ensuring BGM is playing:', currentPage.bgmUrl);
        if (bgm.paused) {
          bgm.play().catch(console.error);
        }
      }
      
      // Set up auto-advance when audio ends (CRITICAL: This updates every time!)
      audio.onended = () => {
        console.log('🎵 Narration ended on page:', currentPageIndex + 1);
        
        // Mark that we're auto-advancing (BGM should continue)
        isAutoAdvancing.current = true;
        setIsPlaying(false);
        
        // CRITICAL FIX: Only pause narration and SFX - NOT BGM during auto-advance!
        sfx?.pause();
        // bgm?.pause(); // REMOVED - let BGM continue during auto-advance!
        
        // Auto-advance to next page if not on last page
        if (bookData && currentPageIndex < bookData.pagesData.length - 1) {
          const currentPage = bookData.pagesData[currentPageIndex];
          const elapsedTime = Date.now() - pageStartTimeRef.current;
          
          // For pages with 3D objects, ensure minimum display time
          if (currentPage.asset3D && elapsedTime < MIN_3D_PAGE_DURATION) {
            const remainingTime = MIN_3D_PAGE_DURATION - elapsedTime;
            console.log(`⏳ 3D page - waiting ${remainingTime}ms before advancing`);
            
            setTimeout(() => {
              console.log('✅ Auto-advancing to page:', currentPageIndex + 2);
              shouldPlayAfterTransition.current = true;
              setIsPageTransitioning(true);
              
              setTimeout(() => {
                setCurrentPageIndex(currentPageIndex + 1);
                setTimeout(() => {
                  setIsPageTransitioning(false);
                }, 600);
              }, 600);
            }, remainingTime);
          } else {
            console.log('✅ Auto-advancing to page:', currentPageIndex + 2);
            
            // Set flag to continue playing after page change
            shouldPlayAfterTransition.current = true;
            
            // Trigger page change with transition
            setIsPageTransitioning(true);
            
            setTimeout(() => {
              setCurrentPageIndex(currentPageIndex + 1);
              
              setTimeout(() => {
                setIsPageTransitioning(false);
              }, 600);
            }, 600);
          }
        } else {
          console.log('📖 Reached last page - showing unified story end modal');
          // Fade out BGM when story ends
          if (bgm && !bgm.paused) {
            const fadeOutInterval = setInterval(() => {
              if (bgm.volume > 0.02) {
                bgm.volume = Math.max(0, bgm.volume - 0.04);
              } else {
                bgm.pause();
                bgm.volume = 0;
                clearInterval(fadeOutInterval);
              }
            }, 50);
          }
          setShowStoryEndModal(true);
        }
      };
    } else {
      // When paused (either manually or during auto-advance)
      audio.pause();
      if (sfx) sfx.pause();
      
      // CRITICAL FIX: Only pause BGM if it's a MANUAL pause (not auto-advance)
      if (!isAutoAdvancing.current && bgm) {
        console.log('[BGM] Manual pause - pausing BGM');
        bgm.pause();
      } else if (isAutoAdvancing.current) {
        console.log('[BGM] Auto-advance - keeping BGM playing');
        // Reset the flag after a short delay (after page transition starts)
        setTimeout(() => {
          isAutoAdvancing.current = false;
        }, 100);
      }
      
      audio.onended = null; // Clear handler when paused
    }

    return () => {
      audio.pause();
      if (sfx) sfx.pause();
      // DON'T pause BGM on cleanup - let it continue
      audio.onended = null; // Cleanup
    };
  }, [isPlaying, currentPage, currentPageIndex, bookData, showCover, isPageTransitioning]);

  // Track page start time for minimum display duration on 3D pages
  useEffect(() => {
    pageStartTimeRef.current = Date.now();
  }, [currentPageIndex]);

  // Netflix-style auto-hide controls in fullscreen mode - Improved UX
  useEffect(() => {
    if (!isFullscreen) {
      // Not in fullscreen, ensure controls are visible
      setShowFullscreenControls(true);
      if (controlsHideTimerRef.current) {
        clearTimeout(controlsHideTimerRef.current);
        controlsHideTimerRef.current = null;
      }
      return;
    }

    console.log('[Fullscreen Controls] 🎬 Netflix-style auto-hide active');

    // Show controls and start hide timer (only called on user interaction)
    const showControlsAndStartTimer = () => {
      setShowFullscreenControls(true);
      
      // Clear existing timer
      if (controlsHideTimerRef.current) {
        clearTimeout(controlsHideTimerRef.current);
      }
      
      // Hide controls after 3 seconds of inactivity
      controlsHideTimerRef.current = setTimeout(() => {
        console.log('[Fullscreen Controls] ⏱️ Hiding controls (3s inactivity)');
        setShowFullscreenControls(false);
      }, 3000);
    };

    // Restart hide timer if controls are currently visible (preserve state during page changes)
    const restartHideTimerIfVisible = () => {
      setShowFullscreenControls((currentlyVisible) => {
        if (currentlyVisible) {
          // Controls are visible, restart the hide timer
          if (controlsHideTimerRef.current) {
            clearTimeout(controlsHideTimerRef.current);
          }
          controlsHideTimerRef.current = setTimeout(() => {
            console.log('[Fullscreen Controls] ⏱️ Hiding controls (3s inactivity)');
            setShowFullscreenControls(false);
          }, 3000);
        }
        // Return current state (no change to visibility)
        return currentlyVisible;
      });
    };

    // Mouse move handler - show controls on movement
    const handleMouseMove = () => {
      showControlsAndStartTimer();
    };

    // Mouse enter handler - show controls when mouse enters
    const handleMouseEnter = () => {
      showControlsAndStartTimer();
    };

    // Handle play/pause state changes
    if (!isPlaying) {
      // Always show controls when paused
      setShowFullscreenControls(true);
      if (controlsHideTimerRef.current) {
        clearTimeout(controlsHideTimerRef.current);
        controlsHideTimerRef.current = null;
      }
    } else {
      // When playing, preserve current visibility state and restart timer if visible
      // This prevents controls from automatically showing on page changes
      restartHideTimerIfVisible();
    }

    // Add event listeners
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      // Cleanup
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseenter', handleMouseEnter);
      if (controlsHideTimerRef.current) {
        clearTimeout(controlsHideTimerRef.current);
        controlsHideTimerRef.current = null;
      }
    };
  }, [isFullscreen, isPlaying]);

  function handleBeginAdventure() {
    // Check if user has access before starting the story
    if (!canAccess) {
      setShowSubscriptionGate(true);
      return;
    }
    
    setIsTransitioning(true);
    setTimeout(() => {
      setShowCover(false);
      setCurrentPageIndex(0);
      // Auto-play when beginning the story
      shouldPlayAfterTransition.current = true;
      setIsTransitioning(false);
    }, 1000);
  }

  function handleNext() {
    if (!bookData || isPageTransitioning) return;
    if (currentPageIndex < bookData.pagesData.length - 1) {
      const wasPlaying = isPlaying;
      
      setIsPageTransitioning(true);
      setIsPlaying(false);
      
      setTimeout(() => {
        setCurrentPageIndex(currentPageIndex + 1);
        if (wasPlaying) {
          shouldPlayAfterTransition.current = true;
        }
        
        setTimeout(() => {
          setIsPageTransitioning(false);
        }, 600);
      }, 600);
    }
  }

  function handlePrevious() {
    if (currentPageIndex > 0 && !isPageTransitioning) {
      const wasPlaying = isPlaying;
      
      setIsPageTransitioning(true);
      setIsPlaying(false);
      
      setTimeout(() => {
        setCurrentPageIndex(currentPageIndex - 1);
        if (wasPlaying) {
          shouldPlayAfterTransition.current = true;
        }
        
        setTimeout(() => {
          setIsPageTransitioning(false);
        }, 600);
      }, 600);
    }
  }

  function togglePlayPause() {
    // Check if user has access before allowing play
    if (!canAccess && !showCover) {
      setShowSubscriptionGate(true);
      return;
    }
    
    // When user manually toggles play/pause, reset auto-advance flag
    isAutoAdvancing.current = false;
    setIsPlaying(!isPlaying);
  }

  function toggleFullscreen() {
    setIsFullscreen(!isFullscreen);
  }

  function goHome() {
    window.location.href = '/audio-book/library';
  }

  function handleAutoPlayNext(nextBookId: string) {
    // Will navigate to next book
    console.log('Auto-playing next book:', nextBookId);
  }

  function handleRepeatStory() {
    // Reset to beginning
    setCurrentPageIndex(0);
    setShowCover(true);
    setShowStoryEndModal(false);
    setIsPlaying(false);
    setIsFullscreen(false);
    shouldPlayAfterTransition.current = false;
    
    // Scroll to top
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function handleStartGeneration(bookId: string, title: string) {
    console.log(`[Viewer] Starting generation tracking for ${title} (${bookId})`);
    setGeneratingBookId(bookId);
  }

  if (loading) {
    return (
      <div style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
        overflow: 'hidden',
      }}>
        {/* Animated Background Gradient */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(102, 126, 234, 0.15) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(118, 75, 162, 0.15) 0%, transparent 50%)',
          animation: 'pulseGlow 8s ease-in-out infinite',
        }} />
        
        {/* Floating Particles */}
        <div style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}>
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: `${Math.random() * 4 + 2}px`,
                height: `${Math.random() * 4 + 2}px`,
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '50%',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${Math.random() * 10 + 15}s linear infinite`,
                animationDelay: `${Math.random() * 5}s`,
                boxShadow: '0 0 10px rgba(255, 255, 255, 0.5)',
              }}
            />
          ))}
        </div>

        {/* Main Loading Content */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '2rem',
        }}>
          {/* Animated Book Icon Container */}
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {/* Glow Ring */}
            <div style={{
              position: 'absolute',
              width: '140px',
              height: '140px',
              borderRadius: '50%',
              border: '3px solid rgba(102, 126, 234, 0.3)',
              animation: 'ringPulse 2s ease-in-out infinite',
            }} />
            
            {/* Inner Glow Ring */}
            <div style={{
              position: 'absolute',
              width: '110px',
              height: '110px',
              borderRadius: '50%',
              border: '2px solid rgba(118, 75, 162, 0.3)',
              animation: 'ringPulse 2s ease-in-out infinite 0.5s',
            }} />

            {/* Icon Container with Glass Effect */}
            <div style={{
              position: 'relative',
              width: '80px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
              animation: 'gentleBounce 2s ease-in-out infinite',
            }}>
              <Sparkles 
                size={40} 
                color="white" 
                style={{
                  filter: 'drop-shadow(0 4px 20px rgba(255, 255, 255, 0.5))',
                  animation: 'sparkleRotate 3s linear infinite',
                }} 
              />
            </div>
          </div>

          {/* Loading Text */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}>
            <h2 style={{
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              fontWeight: 800,
              color: 'white',
              margin: 0,
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              letterSpacing: '0.02em',
            }}>
              Loading Your Story
            </h2>
            
            {/* Animated Dots */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              height: '24px',
            }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    animation: `dotPulse 1.5s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.5)',
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <style jsx>{`
          @keyframes float {
            0%, 100% {
              transform: translateY(0) translateX(0);
              opacity: 0;
            }
            10% {
              opacity: 0.5;
            }
            50% {
              transform: translateY(-100vh) translateX(${Math.random() * 100 - 50}px);
              opacity: 0.8;
            }
            90% {
              opacity: 0.5;
            }
            100% {
              transform: translateY(-200vh) translateX(${Math.random() * 100 - 50}px);
              opacity: 0;
            }
          }
          
          @keyframes ringPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.3;
            }
            50% {
              transform: scale(1.15);
              opacity: 0.6;
            }
          }
          
          @keyframes sparkleRotate {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
          
          @keyframes dotPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.5;
            }
            50% {
              transform: scale(1.5);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  }

  if (!bookData) {
    return <div>Book not found</div>;
  }

  // MAIN VIEWER
  const pageImage = currentPage?.imageUrl || '';
  const pageText = currentPage?.text || '';

  return (
    <div style={{
      minHeight: '100vh',
      background: isFullscreen 
        ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
      backgroundSize: '400% 400%',
      animation: 'gradientFlow 20s ease infinite',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Hidden Audio Elements */}
      <audio ref={audioRef} preload="auto" />
      <audio ref={sfxRef} preload="auto" />
      <audio 
        ref={bgmAudioRef} 
        preload="auto" 
        loop
        onCanPlayThrough={() => setBgmLoaded(true)}
      />

      {/* Floating Particles Background - Fully isolated memoized component */}
      {!isFullscreen && <BackgroundParticles particles={particles} />}

      {/* Header Bar */}
      {!isFullscreen && (
        <div style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          padding: 'clamp(0.5rem, 1.2vh, 0.625rem) clamp(1rem, 3vw, 2rem)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}>
          {/* Home Button */}
          <button
            onClick={goHome}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(15px)',
              border: '1.5px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '50px',
              color: 'white',
              fontSize: 'clamp(0.8125rem, 2vw, 0.9375rem)',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Home size={16} strokeWidth={2.5} />
            Library
          </button>

          {/* Title */}
          <h1 style={{
            fontSize: 'clamp(0.9375rem, 2vw, 1.125rem)',
            fontWeight: 800,
            color: 'white',
            margin: 0,
            textAlign: 'center',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            maxWidth: '500px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {bookData.title}
          </h1>

          {/* Page Counter */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(15px)',
            borderRadius: '50px',
            border: '1.5px solid rgba(255, 255, 255, 0.3)',
          }}>
            <Star size={14} fill="white" color="white" />
            <span style={{
              color: 'white',
              fontSize: 'clamp(0.8125rem, 2vw, 0.9375rem)',
              fontWeight: 700,
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}>
              {currentPageIndex + 1} / {bookData.pagesData.length}
            </span>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: isFullscreen ? '75fr 25fr' : '60fr 40fr',
        gap: isFullscreen ? '0.5rem' : 'clamp(1rem, 2vw, 1.5rem)',
        padding: isFullscreen 
          ? 'clamp(0.75rem, 1.5vh, 1rem) clamp(0.75rem, 1.5vw, 1rem)'
          : 'clamp(1rem, 2vh, 1.5rem) clamp(1rem, 3vw, 2rem) clamp(1rem, 2vh, 1.5rem)',
        alignItems: 'center',
        maxWidth: '100%',
        margin: '0 auto',
        position: 'relative',
      }}>
        {/* Left Side: Image + Controls */}
        <div style={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          opacity: isPageTransitioning ? 0 : 1,
          transform: isPageTransitioning ? 'scale(0.95)' : 'scale(1)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          {/* Story Image */}
          <div style={{
            width: '100%',
            height: 'auto',
            maxHeight: '85vh',
            maxWidth: '85vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            borderRadius: '30px',
            overflow: 'visible',  // ✅ FIXED: Changed from 'hidden' to allow 3D objects to extend beyond container
            boxShadow: isFullscreen 
              ? '0 0 80px rgba(102, 126, 234, 0.6)'
              : '0 20px 60px rgba(0, 0, 0, 0.3)',
          }}>
            <img
              src={pageImage}
              alt={`Page ${currentPageIndex + 1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: '30px',
              }}
            />

            {/* Zoom/Fullscreen Button - TOP RIGHT ON IMAGE */}
            <button
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                zIndex: 120,
                padding: '0.625rem',
                background: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(12px)',
                border: '2px solid rgba(255, 255, 255, 0.35)',
                borderRadius: '12px',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(102, 126, 234, 0.95)';
                e.currentTarget.style.transform = 'scale(1.15)';
                e.currentTarget.style.boxShadow = '0 6px 24px rgba(102, 126, 234, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.75)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.4)';
              }}
            >
              {isFullscreen ? (
                <Minimize2 size={18} strokeWidth={2.5} />
              ) : (
                <Maximize2 size={18} strokeWidth={2.5} />
              )}
            </button>

            {/* Page Atmosphere (z-index: 20) - Color overlay on top of image */}
            {currentPage?.atmosphere && (
              <PageAtmosphere
                gradient={currentPage.atmosphere.gradient}
                mood={currentPage.atmosphere.mood}
                timeOfDay={currentPage.atmosphere.timeOfDay}
                brightness={currentPage.atmosphere.brightness}
              />
            )}

            {/* Featured 3D Asset (if present) - NORMAL VIEW (z-index: 100) */}
            {currentPage?.asset3D && !isPageTransitioning && (
              <>
                <Asset3DRenderer
                  key={`3d-normal-${currentPageIndex}-${currentPage.asset3D.assetId}`}
                  glbUrl={currentPage.asset3D.glbUrl}
                  usdzUrl={currentPage.asset3D.usdzUrl}
                  assetId={currentPage.asset3D.assetId}
                  placement="center"
                  montessoriMode={bookData?.montessoriMode}
                  isPlaying={isPlaying}
                  objectSfxUrl={currentPage.objectSfxUrl}
                  isFirstAssetPage={currentPageIndex === firstAssetPageIndex.current}
                />
              </>
            )}

            {/* Visual Weather Effect (z-index: 55-60) */}
            {currentPage?.visualEffect && currentPage.visualEffect.type !== 'none' && (
              <VisualWeatherEffect
                type={currentPage.visualEffect.type}
                intensity={currentPage.visualEffect.intensity}
              />
            )}

            {/* Magical Effect (z-index: 5 or 50) */}
            {currentPage?.magicalEffect && (
              <MagicalEffect
                effectId={currentPage.magicalEffect.effectId}
              />
            )}

            {/* Epic Moment Effect (z-index: 70) */}
            {currentPage?.epicMoment?.enabled && (
              <EpicMomentEffect
                effect={currentPage.epicMoment.effect}
                trigger={currentPage.epicMoment.trigger}
              />
            )}

            {/* Control Bar Overlay (Bottom of Image) - ALWAYS VISIBLE (z-index: 100) */}
            <div style={{
              position: 'absolute',
              bottom: isFullscreen ? '3.5rem' : '4.5rem',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: isFullscreen ? '0.5rem' : '0.75rem',
              padding: isFullscreen ? '0.5rem 0.75rem' : '0.75rem 1.25rem',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
              backdropFilter: 'blur(25px) saturate(180%)',
              borderRadius: '50px',
              border: '2px solid rgba(255, 255, 255, 0.4)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
              zIndex: 100,
            }}>
              {/* Previous Button */}
              <button
                onClick={handlePrevious}
                disabled={currentPageIndex === 0 || isPageTransitioning}
                style={{
                  padding: isFullscreen ? '0.5rem' : '0.625rem',
                  background: currentPageIndex === 0 
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(255, 255, 255, 0.3)',
                  border: 'none',
                  borderRadius: '50%',
                  color: 'white',
                  cursor: currentPageIndex === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  opacity: currentPageIndex === 0 ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (currentPageIndex > 0) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <ChevronLeft size={isFullscreen ? 20 : 24} strokeWidth={3} />
              </button>

              {/* Play/Pause Button */}
              <button
                onClick={togglePlayPause}
                disabled={isPageTransitioning}
                style={{
                  padding: isFullscreen ? '0.625rem' : '0.875rem',
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                  border: '2px solid rgba(255, 255, 255, 0.6)',
                  borderRadius: '50%',
                  color: '#667eea',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 6px 20px rgba(255, 255, 255, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 100%)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 8px 28px rgba(255, 255, 255, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 255, 255, 0.3)';
                }}
              >
                {isPlaying ? (
                  <Pause size={isFullscreen ? 22 : 28} strokeWidth={3} fill="currentColor" />
                ) : (
                  <Play size={isFullscreen ? 22 : 28} strokeWidth={3} fill="currentColor" style={{ marginLeft: '2px' }} />
                )}
              </button>

              {/* Next Button */}
              <button
                onClick={handleNext}
                disabled={currentPageIndex >= bookData.pagesData.length - 1 || isPageTransitioning}
                style={{
                  padding: isFullscreen ? '0.5rem' : '0.625rem',
                  background: currentPageIndex >= bookData.pagesData.length - 1
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(255, 255, 255, 0.3)',
                  border: 'none',
                  borderRadius: '50%',
                  color: 'white',
                  cursor: currentPageIndex >= bookData.pagesData.length - 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  opacity: currentPageIndex >= bookData.pagesData.length - 1 ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (currentPageIndex < bookData.pagesData.length - 1) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <ChevronRight size={isFullscreen ? 20 : 24} strokeWidth={3} />
              </button>
            </div>
          </div>
        </div>

        {/* Right Side: Text Card */}
        <div style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: isFullscreen ? '0' : 'clamp(1rem, 2vh, 1.5rem) 0',
          opacity: isPageTransitioning ? 0 : 1,
          transform: isPageTransitioning ? 'translateX(20px)' : 'translateX(0)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          {/* Text Container - Large, no-scroll */}
          <div style={{
            background: isFullscreen
              ? 'linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
            backdropFilter: 'blur(25px) saturate(180%)',
            borderRadius: isFullscreen ? '20px' : '28px',
            padding: isFullscreen 
              ? 'clamp(2rem, 3vh, 2.5rem) clamp(2rem, 3vw, 2.5rem)'
              : 'clamp(2.5rem, 4vh, 3.5rem) clamp(2.5rem, 4vw, 3.5rem)',
            border: `2px solid ${isFullscreen ? 'rgba(102, 126, 234, 0.5)' : 'rgba(255, 255, 255, 0.4)'}`,
            boxShadow: isFullscreen
              ? '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              : '0 20px 60px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
            minHeight: isFullscreen ? 'clamp(280px, 35vh, 400px)' : 'clamp(350px, 40vh, 500px)',
            maxHeight: isFullscreen ? 'clamp(380px, 50vh, 550px)' : 'clamp(450px, 55vh, 700px)',
            overflow: 'hidden',
            position: 'relative',
            animation: 'fadeInUp 0.8s ease-out',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}>
            <p style={{
              fontSize: isFullscreen 
                ? 'clamp(1.125rem, 2.5vw, 1.4rem)'
                : 'clamp(1.25rem, 2.75vw, 1.625rem)',
              lineHeight: 1.9,
              color: isFullscreen ? 'rgba(255, 255, 255, 0.95)' : 'white',
              margin: 0,
              fontWeight: 500,
              textShadow: isFullscreen 
                ? '0 2px 8px rgba(0, 0, 0, 0.8)'
                : '0 2px 8px rgba(0, 0, 0, 0.3)',
              textAlign: 'left',
              letterSpacing: '0.01em',
              width: '100%',
            }}>
              <WordHighlightedText
                text={pageText}
                wordTimings={bookData.pagesData[currentPageIndex]?.wordTimings}
                activeWordIndex={activeWordIndex}
                enableWindowing={true}
                windowBefore={0}
                windowAfter={0}
              />
            </p>
          </div>

          {/* LAST PAGE BUTTONS - Show under text on final page */}
          {bookData && currentPageIndex === bookData.pagesData.length - 1 && !showStoryEndModal && (
            <div style={{
              marginTop: '1.5rem',
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              animation: 'slideUp 0.4s ease-out',
            }}>
              <button
                onClick={handleRepeatStory}
                style={{
                  padding: '1rem 1.5rem',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                }}
              >
                <RefreshCw size={20} strokeWidth={2.5} />
                Replay Story
              </button>
              <button
                onClick={() => setShowStoryEndModal(true)}
                style={{
                  padding: '1rem 1.5rem',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  border: 'none',
                  borderRadius: '12px',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }}
              >
                Next Story
                <ArrowRight size={20} strokeWidth={2.5} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* BEGIN ADVENTURE OVERLAY - Shows on top of actual player page */}
      {showCover && !showSubscriptionGate && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.15)', /* Minimal overlay like mobile */
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: isTransitioning ? 'fadeOut 0.4s ease-out forwards' : 'fadeIn 0.3s ease-out',
          }}
        >
          {/* Library Button - Top Left */}
          <button
            onClick={goHome}
            style={{
              position: 'absolute',
              top: 'clamp(1rem, 3vh, 2rem)',
              left: 'clamp(1rem, 3vw, 2rem)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(15px)',
              WebkitBackdropFilter: 'blur(15px)',
              border: '2px solid rgba(255, 255, 255, 0.4)',
              borderRadius: '50px',
              color: 'white',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              zIndex: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 25px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
            }}
          >
            <Home size={18} strokeWidth={2.5} />
            <span>Library</span>
          </button>

          {/* Begin Adventure Button - Center Overlay */}
          <button
            onClick={handleBeginAdventure}
            disabled={isTransitioning}
            style={{
              position: 'relative',
              zIndex: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              padding: '1.5rem 3rem',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.75) 100%)', /* Transparent like mobile */
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: 'none',
              borderRadius: '60px',
              color: '#667eea',
              fontSize: '1.25rem',
              fontWeight: 800,
              cursor: isTransitioning ? 'default' : 'pointer',
              boxShadow: '0 12px 30px rgba(255, 255, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              opacity: isTransitioning ? 0.6 : 1,
              animation: isTransitioning ? 'none' : 'buttonPulse 2s ease-in-out infinite',
            }}
            onMouseEnter={(e) => {
              if (!isTransitioning) {
                e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(255, 255, 255, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isTransitioning) {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(255, 255, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
              }
            }}
          >
            <Play size={48} fill="white" />
            <span>Begin Adventure</span>
          </button>
        </div>
      )}

      <style jsx>{`
        @keyframes gradientFlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeOut {
          to { opacity: 0; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.85); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes scaleOut {
          to { opacity: 0; transform: scale(0.9); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        @keyframes buttonPulse {
          0%, 100% {
            transform: translateY(0) scale(1);
            box-shadow: 0 20px 50px rgba(255, 255, 255, 0.4), inset 0 2px 0 rgba(255, 255, 255, 1);
          }
          50% {
            transform: translateY(-2px) scale(1.02);
            box-shadow: 0 25px 60px rgba(255, 255, 255, 0.5), inset 0 2px 0 rgba(255, 255, 255, 1);
          }
        }
        @keyframes floatOrb1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -40px) scale(1.1); }
        }
        @keyframes floatOrb2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-40px, 30px) scale(1.15); }
        }
        @keyframes floatOrb3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(35px, 35px) scale(1.08); }
        }
        @keyframes gentleBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* UNIFIED STORY END MODAL - All options + countdown timer in one place */}
      {showStoryEndModal && bookData && (
        <UnifiedStoryEndModal
          isOpen={showStoryEndModal}
          onClose={() => setShowStoryEndModal(false)}
          onRepeatStory={handleRepeatStory}
          originalBookId={audioBookId}
          currentBookId={audioBookId}
          storyTitle={bookData.title}
          currentLanguage={bookData.language || 'en'}
          showBilingualOption={true}
          onAutoPlayNext={handleAutoPlayNext}
          onStartGeneration={handleStartGeneration}
        />
      )}

      {/* AUDIO BOOK PROGRESS MODAL - Shows during generation */}
      <AudioBookProgressModal
        isGenerating={!!generatingBookId}
        audioBookId={generatingBookId}
      />


      {/* FULLSCREEN MODAL - Netflix-Style 2026 Experience */}
      {isFullscreen && currentPage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            animation: 'fadeIn 0.3s ease-out',
            overflow: 'hidden',
            cursor: showFullscreenControls ? 'default' : 'none',
            transition: 'cursor 0.3s ease',
          }}
          onClick={(e) => {
            // Show controls when clicking on the fullscreen area (like Netflix)
            // Only if clicking on the background, not on buttons
            if (e.target === e.currentTarget) {
              setShowFullscreenControls(true);
              // Clear existing timer
              if (controlsHideTimerRef.current) {
                clearTimeout(controlsHideTimerRef.current);
              }
              // Start new hide timer
              controlsHideTimerRef.current = setTimeout(() => {
                if (isPlaying) {
                  setShowFullscreenControls(false);
                }
              }, 3000);
            }
          }}
        >
          {/* Ambient Glow Background */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 50% 40%, rgba(102, 126, 234, 0.12) 0%, transparent 70%)',
            animation: 'pulseGlow 12s ease-in-out infinite',
            pointerEvents: 'none',
          }} />

          {/* Exit Button - Top Right (Netflix-style Auto-hide) */}
          <button
            onClick={() => setIsFullscreen(false)}
            aria-label="Exit fullscreen (ESC)"
            style={{
              position: 'absolute',
              top: 'clamp(1rem, 2vh, 1.5rem)',
              right: 'clamp(1rem, 2vw, 1.5rem)',
              width: 'clamp(44px, 5vw, 52px)',
              height: 'clamp(44px, 5vw, 52px)',
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              zIndex: 1000,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              opacity: showFullscreenControls ? 1 : 0,
              pointerEvents: showFullscreenControls ? 'auto' : 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
              e.currentTarget.style.transform = 'scale(1.1) rotate(-90deg)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
              e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.3)';
            }}
          >
            <Minimize2 size={clamp(20, 24, 28)} color="white" strokeWidth={2.5} />
          </button>

          {/* Page Counter - Top Left (Netflix-style Auto-hide) */}
          <div style={{
            position: 'absolute',
            top: 'clamp(1rem, 2vh, 1.5rem)',
            left: 'clamp(1rem, 2vw, 1.5rem)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1rem',
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(20px)',
            borderRadius: '50px',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            zIndex: 1000,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            opacity: showFullscreenControls ? 1 : 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: showFullscreenControls ? 'auto' : 'none',
          }}>
            <Volume2 size={16} color="white" strokeWidth={2.5} />
            <span style={{
              color: 'white',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: 700,
            }}>
              {currentPageIndex + 1} / {bookData.pagesData.length}
            </span>
          </div>

          {/* Main Content - Vertical Stack (Award-Winning 2026 Layout) */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: 'clamp(3.5rem, 7vh, 4.5rem) 0 0',
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}>
            {/* Image Section - Top (82vh - Larger for 2026 Award-Winning Experience) */}
            <div 
              style={{
                position: 'relative',
                height: '82vh',
                width: '95%',
                maxWidth: '82vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isPageTransitioning ? 0 : 1,
                transform: isPageTransitioning ? 'scale(0.95)' : 'scale(1)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
                overflow: 'visible',  // ✅ FIXED: Changed from 'hidden' to allow 3D objects to extend beyond container in fullscreen
                borderRadius: '30px',
                marginBottom: 'clamp(0.75rem, 1.5vh, 1rem)',
                background: '#000',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                // Show controls when clicking on the image area
                e.stopPropagation();
                setShowFullscreenControls(true);
                // Clear existing timer
                if (controlsHideTimerRef.current) {
                  clearTimeout(controlsHideTimerRef.current);
                }
                // Start new hide timer
                controlsHideTimerRef.current = setTimeout(() => {
                  if (isPlaying) {
                    setShowFullscreenControls(false);
                  }
                }, 3000);
              }}
            >
              <img
                src={pageImage}
                alt={`Page ${currentPageIndex + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: '30px',
                  boxShadow: '0 40px 100px rgba(0, 0, 0, 0.7)',
                  border: '3px solid rgba(255, 255, 255, 0.15)',
                }}
              />

              {/* Page Atmosphere (z-index: 20) - Color overlay on top of image - FULLSCREEN */}
              {currentPage?.atmosphere && (
                <PageAtmosphere
                  gradient={currentPage.atmosphere.gradient}
                  mood={currentPage.atmosphere.mood}
                  timeOfDay={currentPage.atmosphere.timeOfDay}
                  brightness={currentPage.atmosphere.brightness}
                />
              )}
              {/* Featured 3D Asset (if present) - FULLSCREEN (z-index: 100) */}
              {currentPage?.asset3D && !isPageTransitioning && (
                <>
                  <Asset3DRenderer
                    key={`3d-fullscreen-${currentPageIndex}-${currentPage.asset3D.assetId}`}
                    glbUrl={currentPage.asset3D.glbUrl}
                    usdzUrl={currentPage.asset3D.usdzUrl}
                    assetId={currentPage.asset3D.assetId}
                    placement="center"
                    montessoriMode={bookData?.montessoriMode}
                    isPlaying={isPlaying}
                    objectSfxUrl={currentPage.objectSfxUrl}
                    isFirstAssetPage={currentPageIndex === firstAssetPageIndex.current}
                  />
                </>
              )}

              {/* Visual Weather Effect (z-index: 55-60) - FULLSCREEN */}
              {currentPage?.visualEffect && currentPage.visualEffect.type !== 'none' && (
                <VisualWeatherEffect
                  type={currentPage.visualEffect.type}
                  intensity={currentPage.visualEffect.intensity}
                />
              )}

              {/* Magical Effect (z-index: 5 or 50) - FULLSCREEN */}
              {currentPage?.magicalEffect && (
                <MagicalEffect
                  effectId={currentPage.magicalEffect.effectId}
                />
              )}

              {/* Epic Moment Effect (z-index: 70) - FULLSCREEN */}
              {currentPage?.epicMoment?.enabled && (
                <EpicMomentEffect
                  effect={currentPage.epicMoment.effect}
                  trigger={currentPage.epicMoment.trigger}
                />
              )}

              {/* Bottom Controls Bar - Netflix-style Auto-hide */}
              <div style={{
                position: 'absolute',
                bottom: 'clamp(1.5rem, 3vh, 2rem)',
                left: '50%',
                transform: `translateX(-50%) translateY(${showFullscreenControls ? '0' : '120%'})`,
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(1rem, 2vw, 1.5rem)',
                padding: 'clamp(0.75rem, 1.5vh, 1rem) clamp(1.5rem, 3vw, 2rem)',
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(25px)',
                borderRadius: '60px',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
                opacity: showFullscreenControls ? 1 : 0,
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                zIndex: 1000,
                pointerEvents: showFullscreenControls ? 'auto' : 'none',
              }}>
                {/* Previous Button */}
                <button
                  onClick={handlePrevious}
                  disabled={currentPageIndex === 0 || isPageTransitioning}
                  style={{
                    width: 'clamp(48px, 6vw, 56px)',
                    height: 'clamp(48px, 6vw, 56px)',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    background: currentPageIndex === 0 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: currentPageIndex === 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: currentPageIndex === 0 ? 0.4 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (currentPageIndex > 0) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <ChevronLeft size={clamp(24, 28, 32)} color="white" strokeWidth={2.5} />
                </button>

                {/* Play/Pause Button */}
                <button
                  onClick={togglePlayPause}
                  style={{
                    width: 'clamp(56px, 7vw, 64px)',
                    height: 'clamp(56px, 7vw, 64px)',
                    borderRadius: '50%',
                    border: '3px solid rgba(255, 255, 255, 0.4)',
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.5)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.7)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.5)';
                  }}
                >
                  {isPlaying ? (
                    <Pause size={clamp(26, 30, 34)} color="white" strokeWidth={2.5} fill="white" />
                  ) : (
                    <Play size={clamp(26, 30, 34)} color="white" strokeWidth={2.5} fill="white" style={{ marginLeft: '4px' }} />
                  )}
                </button>

                {/* Next Button */}
                <button
                  onClick={handleNext}
                  disabled={currentPageIndex >= bookData.pagesData.length - 1 || isPageTransitioning}
                  style={{
                    width: 'clamp(48px, 6vw, 56px)',
                    height: 'clamp(48px, 6vw, 56px)',
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    background: currentPageIndex >= bookData.pagesData.length - 1 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: currentPageIndex >= bookData.pagesData.length - 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: currentPageIndex >= bookData.pagesData.length - 1 ? 0.4 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (currentPageIndex < bookData.pagesData.length - 1) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <ChevronRight size={clamp(24, 28, 32)} color="white" strokeWidth={2.5} />
                </button>
              </div>
            </div>

            {/* Caption Section - Award-Winning Single Line Display */}
            <div style={{
              flex: 0,
              width: '95%',
              maxWidth: '1400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'clamp(1.25rem, 2.5vh, 1.75rem) clamp(1.5rem, 3vw, 2rem)',
              background: 'linear-gradient(180deg, rgba(26, 26, 46, 0.97) 0%, rgba(22, 33, 62, 1) 100%)',
              minHeight: '100px',
              borderRadius: '0 0 24px 24px',
              boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.4)',
              opacity: isPageTransitioning ? 0 : 1,
              transition: 'opacity 0.6s ease',
              overflow: 'visible',
            }}>
              <WordByWordCaption
                wordTimings={bookData.pagesData[currentPageIndex]?.wordTimings || []}
                currentTime={currentTimeRef.current}
                isPlaying={isPlaying}
              />
            </div>
          </div>
        </div>
      )}

      {/* Subscription Gate */}
      {showSubscriptionGate && (
        <SubscriptionGate
          isAuthenticated={false} // Will be checked inside component
          currentPath={`/audio-book/${audioBookId}`}
        />
      )}
    </div>
  );
}

function clamp(min: number, val: number, max: number) {
  return Math.min(Math.max(min, val), max);
}

// Helper function to find the active word index based on current time
// IMPROVED: Better tolerance, handles gaps between words, looks ahead and behind
function findActiveWordIndex(currentTime: number, wordTimings: Array<{ w: string; start: number; end: number }>): number {
  if (!wordTimings || wordTimings.length === 0) {
    return -1;
  }
  
  // TIMING TOLERANCE: Account for audio sync issues and gaps
  const LOOK_AHEAD = 0.15;   // 150ms look-ahead (increased from 100ms)
  const LOOK_BEHIND = 0.05;  // 50ms look-behind (NEW: forgives slight audio delays)
  const GAP_TOLERANCE = 0.2; // 200ms tolerance for gaps between words
  
  // STEP 1: Try exact match with tolerance
  // Check if current time is within word's time range (with look-behind tolerance)
  for (let i = 0; i < wordTimings.length; i++) {
    const timing = wordTimings[i];
    // Use <= instead of < to include end boundary
    // Add look-behind to catch slightly delayed playback
    if (currentTime >= (timing.start - LOOK_BEHIND) && currentTime <= timing.end) {
      return i;
    }
  }
  
  // STEP 2: Check for gaps between words (current time is between two words)
  // This handles cases where audio has slight gaps or timing imperfections
  for (let i = 0; i < wordTimings.length - 1; i++) {
    const current = wordTimings[i];
    const next = wordTimings[i + 1];
    
    // If we're in the gap between words
    if (currentTime > current.end && currentTime < next.start) {
      const gap = next.start - current.end;
      
      // If gap is small (< GAP_TOLERANCE), decide which word to show based on proximity
      if (gap < GAP_TOLERANCE) {
        const distanceFromCurrent = currentTime - current.end;
        const distanceToNext = next.start - currentTime;
        
        // Show the closer word (whichever is nearer)
        return distanceToNext < distanceFromCurrent ? i + 1 : i;
      } else {
        // Large gap: show the next word if it's coming soon
        if (next.start - currentTime < LOOK_AHEAD) {
          return i + 1;
        }
        // Otherwise keep showing current word
        return i;
      }
    }
  }
  
  // STEP 3: Look ahead - find next upcoming word within tolerance
  for (let i = 0; i < wordTimings.length; i++) {
    if (wordTimings[i].start > currentTime && 
        wordTimings[i].start <= currentTime + LOOK_AHEAD) {
      return i;
    }
  }
  
  // STEP 4: Fall back to most recent word that has started
  for (let i = wordTimings.length - 1; i >= 0; i--) {
    if (currentTime >= wordTimings[i].start) {
      return i;
    }
  }
  
  // STEP 5: If before all words, return -1 (preview state)
  if (currentTime < wordTimings[0].start) {
    return -1;
  }
  
  return -1;
}

